import React, { createContext, Dispatch, ReactNode, useContext, useReducer } from 'react';

import { findIndex, isUndefined } from 'lodash';

import { ReplyTo, ReplyMode } from 'reviews2/feed/review-yeti/Review.types';

const RepliesStateContext = createContext<RepliesContainer | undefined>(undefined);
const RepliesDispatchContext = createContext<Dispatch<RepliesAction> | undefined>(undefined);

interface RepliesContainer {
    replies: any[];
    newReplies: any[]; // replies created after initial page load
    currentReply?: any;
    replyTo?: ReplyTo;
    replyMode?: ReplyMode;
    editReply?: any;
    canPublish?: boolean;
    disableModeration?: boolean;
}

export interface RepliesAction {
    type: RepliesActionType;
    reply?: any;
    replyTo?: ReplyTo;
    replies?: any[];
    replyMode?: ReplyMode;
    canPublish?: boolean;
    disableModeration?: boolean;
}

export enum RepliesActionType {
    SetReply,
    SetReplies,
    SetReplyTo,
    SetReplyMode,
    SetEditReply,
    AddReply,
    AddNewReply,
    UpdateReply,
    RemoveReply,
    SetCanPublish,
    ToggleModeration,
}

interface RepliesProviderProps {
    children: ReactNode;
    replies: any[];
}

const repliesReducer = (repliesContainer: RepliesContainer, action: RepliesAction): RepliesContainer => {
    switch (action.type) {
        case RepliesActionType.SetReply: {
            if (!action.reply) {
                throw new Error('SetReply: required action arg missing: reply');
            }

            return { ...repliesContainer, currentReply: action.reply };
        }
        case RepliesActionType.SetReplies: {
            if (!action.replies) {
                throw new Error('SetReplies: required action arg missing: replies');
            }

            return { ...repliesContainer, replies: action.replies };
        }
        case RepliesActionType.SetReplyTo: {
            if (!action.replyTo) {
                throw new Error('SetReplyTo: required action arg missing: replyTo');
            }

            return { ...repliesContainer, replyTo: action.replyTo };
        }
        case RepliesActionType.SetReplyMode: {
            if (!action.replyMode) {
                throw new Error('SetReplyMode: required action arg missing: mode');
            }

            return { ...repliesContainer, replyMode: action.replyMode };
        }
        case RepliesActionType.AddReply: {
            if (!action.reply) {
                throw new Error('AddReply: required action arg missing: reply');
            }

            const { replies } = repliesContainer;
            const parentIndex = findIndex(replies, { id: action.reply.parent_id });

            // check for nested replies
            if (parentIndex >= 0) {
                const parent = replies[parentIndex];

                parent.replies = parent.replies || [];
                parent.replies.push(action.reply);

                replies.splice(parentIndex, 1, parent);
            } else {
                replies.push(action.reply);
            }

            return { ...repliesContainer, replies: [...replies] };
        }
        case RepliesActionType.AddNewReply: {
            if (!action.reply) {
                throw new Error('AddNewReply: required action arg missing: reply');
            }

            return { ...repliesContainer, newReplies: [...repliesContainer.newReplies, action.reply] };
        }
        case RepliesActionType.UpdateReply: {
            if (!action.reply) {
                throw new Error('UpdateReply: required action arg missing: reply');
            }

            const updatedReplies = [...repliesContainer.replies].map((reply) => {
                if (reply.document_id === action.reply?.document_id) {
                    return { ...reply, ...action.reply };
                }

                if (reply.replies?.length) {
                    // eslint-disable-next-line
                    reply.replies = reply.replies.map((nestedReply: any) => {
                        if (nestedReply.document_id === action.reply?.document_id) {
                            return { ...nestedReply, ...action.reply };
                        }
                        return nestedReply;
                    });
                }

                return reply;
            });

            return { ...repliesContainer, replies: updatedReplies };
        }
        case RepliesActionType.SetEditReply: {
            return { ...repliesContainer, editReply: action.reply };
        }
        case RepliesActionType.RemoveReply: {
            if (!action.reply) {
                throw new Error('RemoveReply: required action arg missing: reply');
            }

            const updatedReplies = [...repliesContainer.replies].filter((reply) => {
                if (reply.document_id === action.reply?.document_id) {
                    return false;
                }

                if (reply.replies?.length) {
                    // eslint-disable-next-line
                    reply.replies = reply.replies.filter((nestedReply: any) => {
                        if (nestedReply.document_id === action.reply?.document_id) {
                            return false;
                        }
                        return true;
                    });
                }

                return true;
            });

            return { ...repliesContainer, replies: updatedReplies };
        }
        case RepliesActionType.SetCanPublish: {
            if (isUndefined(action.canPublish)) {
                throw new Error('SetCanPublish: required action arg missing: canPublish');
            }

            return { ...repliesContainer, canPublish: action.canPublish };
        }
        case RepliesActionType.ToggleModeration: {
            return { ...repliesContainer, disableModeration: !repliesContainer.disableModeration };
        }
        default:
            throw new Error(`Replies: unhandled action: ${action.type}`);
    }
};

export const RepliesProvider = ({ children, replies }: RepliesProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(repliesReducer, {
        replies: replies || [],
        newReplies: [], // replies created during the current session
        replyTo: ReplyTo.Review,
        replyMode: ReplyMode.Add,
    });

    return (
        <RepliesStateContext.Provider value={state}>
            <RepliesDispatchContext.Provider value={dispatch}>{children}</RepliesDispatchContext.Provider>
        </RepliesStateContext.Provider>
    );
};

export const useRepliesState = (): RepliesContainer => {
    const context = useContext(RepliesStateContext);

    if (context === undefined) {
        throw new Error('useRepliesState must be used within a RepliesProvider');
    }

    return context;
};

export const useRepliesDispatch = (): Dispatch<RepliesAction> => {
    const context = useContext(RepliesDispatchContext);

    if (context === undefined) {
        throw new Error('useRepliesDispatch must be used within a RepliesProvider');
    }

    return context;
};

export const useReplies = (): [RepliesContainer, Dispatch<RepliesAction>] => {
    return [useRepliesState(), useRepliesDispatch()];
};
